
import Vue, { PropType } from 'vue';
import { get } from 'lodash';
import Api from '@/core/api';
import Btn from '@/common/button/Button.vue';
import Icon, { Icon as IconName } from '@/common/icon/Icon.vue';
import { Unsubscriber } from '@/core/event-bus';
import Slider from '@/common/slider/Slider.vue';
import { PlayerState } from '@/core/player-state';

enum VolumeLevel {
    muted = 0,
    low = 1,
    mid = 2,
    high = 3
}

export default Vue.extend({
    components: {
        Btn,
        Icon,
        Slider
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            volume: null as number | null,
            videoVolume: NaN,
            muted: true,
            unsubscribers: [] as Unsubscriber[]
        };
    },

    computed: {
        volumeLevel(): VolumeLevel {
            if (this.muted) {
                return VolumeLevel.muted;
            }

            if (!this.volume || this.volume < 0) {
                return VolumeLevel.muted;
            }

            if (this.volume < 0.4) {
                return VolumeLevel.low;
            }

            if (this.volume <= 0.6) {
                return VolumeLevel.mid;
            }

            return VolumeLevel.high;
        },

        icon(): IconName {
            switch (this.volumeLevel) {
                case VolumeLevel.muted:
                    return IconName.volumeOff;
                case VolumeLevel.low:
                    return IconName.volumeLow;
                case VolumeLevel.mid:
                    return IconName.volumeMid;
                case VolumeLevel.high:
                default:
                    return IconName.volumeMax;
            }
        }
    },

    mounted() {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.updateState(partial);
            }
        );

        this.updateState(state);

        this.unsubscribers.push(unsubscriber);
    },

    beforeDestroy() {
        for (const unsubscriber of this.unsubscribers) {
            if (typeof unsubscriber === 'function') {
                unsubscriber();
            }
        }
    },

    methods: {
        onVolumeSliderChanged(newVolume: number) {
            if (this.muted) {
                this.api.unmute();
            }

            this.api.changeVolume(newVolume);
        },

        toggleMute() {
            if (this.muted) {
                this.api.unmute();
            } else {
                this.api.mute();
            }
        },

        updateState(state: Partial<PlayerState>) {
            this.muted = get(state, 'muted', this.muted);
            this.videoVolume = get(state, 'volume', this.videoVolume);

            this.volume = this.muted
                ? 0
                : this.videoVolume;
        }
    }
});
